import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '../hooks/useTheme'
import { useEspacios } from '../hooks/useEspacios'
import { useTiposPublicos } from '../hooks/useTiposPublicos'
import { getImageUrl } from '../utils/imageUtils'
import { slugify } from '../utils/stringHelpers'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorMessage from '../components/common/ErrorMessage'
import Footer from '../components/common/Footer'
import AnimatedCounter from '../components/common/AnimatedCounter'
import { ScrollProgressIndicator } from '../components/common/ScrollProgressIndicator'
import { TransitionMapSystemWithAPI } from '../components/map'

const HomePage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [currentSection, setCurrentSection] = useState(0)
  const [heroImages, setHeroImages] = useState<string[]>([])
  const [loadingHeroImages, setLoadingHeroImages] = useState(true)
  const [actividadesImages, setActividadesImages] = useState<Record<string, string>>({})
  const [totalEspacios, setTotalEspacios] = useState<number>(0)
  const [totalReservas, setTotalReservas] = useState<number>(0)

  // Obtener tipos de espacios
  const { data: tiposEspacios = [] } = useTiposPublicos()

  // Query para obtener espacios
  const {
    data: espacios,
    isLoading: loadingEspacios,
    error: errorEspacios,
    refetch: refetchEspacios
  } = useEspacios()

  // ✅ Cargar imágenes hero dinámicamente desde el backend
  useEffect(() => {
    const fetchHeroImages = async () => {
      try {
        setLoadingHeroImages(true)
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/espacios/hero-images`)

        if (!response.ok) {
          throw new Error('Error al cargar imágenes hero')
        }

        const images: string[] = await response.json()

        if (!Array.isArray(images) || images.length === 0) {
          throw new Error('No se encontraron imágenes hero')
        }

        // Construir URLs completas
        const fullUrls = images.map(img => `${import.meta.env.VITE_API_URL}${img}`)
        setHeroImages(fullUrls)
      } catch (error) {
        console.error('Error cargando imágenes hero:', error)
        // Fallback: si falla, usar imágenes por defecto
        setHeroImages([
          `${import.meta.env.VITE_API_URL}/static/uploads/hero/placeholder-hero-1.svg`,
          `${import.meta.env.VITE_API_URL}/static/uploads/hero/placeholder-hero-2.svg`,
          `${import.meta.env.VITE_API_URL}/static/uploads/hero/placeholder-hero-3.svg`,
        ])
      } finally {
        setLoadingHeroImages(false)
      }
    }

    fetchHeroImages()
  }, [])

  // ✅ Cargar métricas dinámicamente desde el backend
  useEffect(() => {
    const fetchMetricas = async () => {
      try {
        // Obtener total de espacios
        const espaciosResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/espacios/metricas/total-espacios`)
        if (espaciosResponse.ok) {
          const espaciosData = await espaciosResponse.json()
          setTotalEspacios(espaciosData.total || 0)
        }

        // Obtener total de reservas
        const reservasResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/espacios/metricas/total-reservas`)
        if (reservasResponse.ok) {
          const reservasData = await reservasResponse.json()
          setTotalReservas(reservasData.total || 0)
        }
      } catch (error) {
        console.error('Error cargando métricas:', error)
      }
    }

    fetchMetricas()
  }, [])

  // ✅ Cargar imágenes de actividades dinámicamente desde el backend
  useEffect(() => {
    const fetchActividadesImages = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/espacios/actividades-images`)

        if (!response.ok) {
          throw new Error('Error al cargar imágenes de actividades')
        }

        const result: any = await response.json()

        // Extraer datos del wrapper UAU si existe
        const images = result.data || result

        // Construir URLs completas
        const fullUrls: Record<string, string> = {}
        for (const [key, url] of Object.entries(images)) {
          fullUrls[key] = `${import.meta.env.VITE_API_URL}${url}`
        }
        setActividadesImages(fullUrls)
      } catch (error) {
        console.error('Error cargando imágenes de actividades:', error)
        // Si falla, dejar vacío - las imágenes tienen fallback en el renderizado
        setActividadesImages({})
      }
    }

    fetchActividadesImages()
  }, [])


  // Secciones de navegación para el botón "Descubre más"
  const sections = ['buscar', 'espacios', 'mapa', 'experiencias', 'contacto']

  // Rotación automática de imágenes (solo si hay imágenes)
  useEffect(() => {
    if (heroImages.length === 0) return

    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [heroImages.length])

  // Detectar sección actual al hacer scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i])
        if (element && element.offsetTop <= scrollPosition) {
          setCurrentSection(i)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Función para ir a la siguiente sección
  const scrollToNextSection = () => {
    const nextIndex = Math.min(currentSection + 1, sections.length - 1)
    document.getElementById(sections[nextIndex])?.scrollIntoView({ behavior: 'smooth' })
  }

  // Determinar qué datos mostrar
  const datosAMostrar = espacios
  const isLoading = loadingEspacios
  const error = errorEspacios
  const refetch = refetchEspacios

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
        <nav className="glass-light px-4 sm:px-6 py-3 sm:py-4 backdrop-blur-md bg-black/20">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img
                src={`${import.meta.env.VITE_API_URL}/static/uploads/logo/logo.png`}
                alt="Camping Playa Niklitschek Logo"
                className="h-8 sm:h-10 w-auto"
              />
              <div className="hidden sm:block">
                <h1 className="font-display font-bold text-base sm:text-xl text-white">Camping Playa Niklitschek</h1>
                <p className="text-white/80 text-xs sm:text-sm">Lago Llanquihue • Región de Los Lagos</p>
              </div>
            </div>

            <div className="hidden lg:flex items-center space-x-8 text-white">
              <a href="#inicio" className="hover:text-camping-200 transition-colors">Inicio</a>
              <a href="#espacios" className="hover:text-camping-200 transition-colors">Espacios</a>
              <a href="#mapa" className="hover:text-camping-200 transition-colors">Mapa</a>
              <button
                onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
                className="hover:text-camping-200 transition-colors bg-none border-none p-0 cursor-pointer text-white"
              >
                Contacto
              </button>
            </div>

            <div className="flex items-center">
              <a
                href="/reservar"
                className="btn-ghost text-xs sm:text-sm px-3 sm:px-4 py-2 transition-colors"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#81d4fa'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'white'
                }}
              >
                Reservar
              </a>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="inicio" className="hero-modern relative">
        <div className="absolute inset-0 overflow-hidden">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
              style={{
                backgroundImage: `url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          ))}
          <div className="hero-overlay" />
          <div className="absolute inset-0 bg-pattern-camping" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center text-white">
          <div className="animate-fade-in">
            <div className="mb-4 sm:mb-6">
              <h2 className="font-display font-bold text-4xl sm:text-5xl text-white">
                Camping Playa
              </h2>
              <h1 className="font-display font-bold text-7xl sm:text-8xl mb-4 sm:mb-6 leading-tight text-gradient-sunset bg-clip-text text-transparent">
                Niklitschek
              </h1>
            </div>

            <p className="text-xs sm:text-sm md:text-sm lg:text-sm font-light mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed px-4">
              Vive la experiencia de camping más auténtica del Lago Llanquihue.
              Con vistas únicas a los volcanes Osorno y Calbuco, cada día se convierte en una aventura inolvidable.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-8 sm:mb-12 px-4">
              <button
                onClick={() => navigate('/reservar')}
                className="w-full sm:w-auto px-8 py-3 rounded-lg font-semibold text-white transition-colors shadow-lg"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primaryDark
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary
                }}
              >
                🏕️ Reservar Ahora
              </button>
              <button
                className="w-full sm:w-auto px-8 py-3 rounded-lg font-semibold text-white transition-colors"
                style={{
                  backgroundColor: 'transparent',
                  borderWidth: '2px',
                  borderColor: 'white',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                onClick={() => document.getElementById('espacios')?.scrollIntoView({ behavior: 'smooth' })}
              >
                📋 Ver Catálogo
              </button>
              <button
                className="hidden sm:block w-full sm:w-auto px-8 py-3 rounded-lg font-semibold text-white transition-colors"
                style={{
                  backgroundColor: 'transparent',
                  borderWidth: '2px',
                  borderColor: 'white',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
                onClick={() => document.getElementById('mapa')?.scrollIntoView({ behavior: 'smooth' })}
              >
                🗺️ Explorar Mapa
              </button>
            </div>
          </div>
        </div>

        <div className="floating-particles"></div>
      </section>

      {/* Sistema de Mapas con API Real */}
      <section id="mapa" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">

          {/* Sistema de Transición con API Real */}
          <TransitionMapSystemWithAPI height="600px" />
        </div>
      </section>

      {/* Espacios Section - Tipos de Espacios */}
      <section id="espacios" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gradient-camping mb-4 sm:mb-6">
              Nuestros Espacios
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Desde cabañas familiares premium hasta sitios de camping con todas las comodidades,
              cada espacio ofrece vistas privilegiadas al lago y la experiencia auténtica del camping familiar.
            </p>
          </div>

          {/* Estados de carga y error */}
          {isLoading && (
            <div className="py-16">
              <LoadingSpinner size="lg" text="Cargando espacios disponibles..." />
            </div>
          )}

          {error && (
            <ErrorMessage
              message={`Error al cargar espacios: ${error instanceof Error ? error.message : 'Error desconocido'}`}
              onRetry={() => refetch()}
              className="mb-8"
            />
          )}

          {/* Tarjetas de Tipos de Espacios - Dinámicas */}
          {!isLoading && !error && espacios && (
            <div>
              <div className="flex flex-wrap justify-center gap-6 mb-12">
                {/* Extraer tipos únicos directamente de tiposEspacios - Solo mostrar públicos (reservables y no reservables) */}
                {tiposEspacios
                  .filter(tipo => !tipo.privado)
                  .map((tipo) => {
                    const tipoSlug = slugify(tipo.nombre || 'desconocido')

                    // Contar espacios de este tipo
                    const count = espacios.filter(e => e.tipo_nombre === tipo.nombre).length

                    // Asignar iconos dinámicamente según el tipo (fallback si no hay imagen)
                    const getIcon = (nombre: string) => {
                      const lower = nombre?.toLowerCase() || ''
                      if (lower.includes('cabaña')) return '🏡'
                      if (lower.includes('camping')) return '🏕️'
                      if (lower.includes('mesón')) return '🌲'
                      return '🏠'
                    }

                    return (
                      <button
                        key={tipo.id}
                        onClick={() => navigate(`/reservar/${tipoSlug}`)}
                        className="group relative overflow-hidden rounded-xl hover-lift transform transition-all duration-300 hover:shadow-2xl text-left w-full sm:w-64 h-64"
                      >
                        {/* Imagen de fondo si existe */}
                        {tipo.imagen ? (
                          <>
                            <img
                              src={getImageUrl(tipo.imagen)}
                              alt={tipo.nombre}
                              className="absolute inset-0 w-full h-full object-cover"
                              onError={(e) => {
                                // Si falla la carga, ocultar y mostrar fallback
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                            {/* Overlay oscuro para legibilidad del texto */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                          </>
                        ) : (
                          /* Fallback: gradient si no hay imagen */
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                            <div className="text-8xl opacity-20">{getIcon(tipo.nombre)}</div>
                          </div>
                        )}

                        {/* Contenido encima del fondo */}
                        <div className="relative z-10 p-6 h-full flex flex-col justify-between">
                          <div>
                            <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-camping-200 transition-colors">
                              {tipo.nombre}
                            </h3>
                          </div>

                          <div>
                            <div className="flex items-center justify-between">
                              <span className="text-3xl font-bold text-white">
                                {count}
                              </span>
                            </div>
                            <div className="mt-4 pt-4 border-t border-white/20 flex items-center text-white group-hover:text-camping-200 font-medium text-sm">
                              Explorar
                              <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </button>
                    )
                  })}
              </div>

              {/* Sección de descripción adicional */}
              <div className="bg-gradient-to-r from-camping-50 to-camping-50 rounded-2xl p-8 md:p-12 border border-camping-100">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                      Elige tu experiencia perfecta
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Cada tipo de espacio ofrece una experiencia única en el Lago Llanquihue.
                      Desde cabañas con todas las comodidades hasta camping tradicional,
                      encontrarás la opción perfecta para tu próximo viaje.
                    </p>
                    <ul className="space-y-3 text-sm text-gray-700">
                      <li className="flex items-center space-x-2">
                        <span className="text-camping-600">✓</span>
                        <span>Vistas panorámicas al lago y volcanes</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <span className="text-camping-600">✓</span>
                        <span>Acceso a servicios y comodidades</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <span className="text-camping-600">✓</span>
                        <span>Ambiente familiar y seguro</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <span className="text-camping-600">✓</span>
                        <span>Disponibilidad en tiempo real</span>
                      </li>
                    </ul>
                  </div>
                  <div className="text-center">
                    <div className="text-8xl mb-4">🏕️</div>
                    <button
                      onClick={() => navigate('/reservar')}
                      className="inline-block px-8 py-3 rounded-lg font-semibold text-white transition-colors"
                      style={{ backgroundColor: colors.primary }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primaryDark
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = colors.primary
                      }}
                    >
                      Consultar Disponibilidad
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Experiencias Section */}
      {/* COMENTADO: Sección de Actividades en el Lago */}
      {/*
      <section id="experiencias" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gradient-camping mb-4 sm:mb-6">
              Actividades en el Lago
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Disfruta de actividades familiares en uno de los lagos más hermosos de Chile,
              rodeado de volcanes y naturaleza prístina.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                key: 'pesca',
                title: 'Pesca en el Lago',
                description: 'Pesca deportiva en las aguas cristalinas del Llanquihue',
                icon: '🎣'
              },
              {
                key: 'kayak',
                title: 'Kayak Familiar',
                description: 'Explora las orillas del lago en kayak con toda la familia',
                icon: '🛶'
              },
              {
                key: 'senderismo',
                title: 'Senderismo',
                description: 'Caminas naturales rodeados de volcanes y naturaleza prístina',
                icon: '🥾'
              },
              {
                key: 'natacion',
                title: 'Natación',
                description: 'Disfruta de las aguas cristalinas del lago en familia',
                icon: '🏊'
              }
            ].map((actividad, index) => (
              <div
                key={index}
                className="card-modern overflow-hidden animate-slide-up hover-lift"
                style={{ animationDelay: `${index * 200}ms` }}
              >
                <div className="relative h-48">
                  <img
                    src={actividadesImages[actividad.key] || `${import.meta.env.VITE_API_URL}/static/uploads/actividades/${actividad.key}.svg`}
                    alt={actividad.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4 text-white">
                    <div className="text-3xl mb-2">{actividad.icon}</div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {actividad.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {actividad.description}
                  </p>
                  <button className="text-camping-600 font-semibold hover:text-camping-700 transition-colors">
                    Saber más →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Stats Section - Movida aquí desde su ubicación anterior */}
      <section id="experiencias" className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Primera fila: Estadísticas generales */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 text-center mb-8 sm:mb-12">
            <AnimatedCounter target={50} label="Años en el lago" icon="🏕️" duration={2500} />
            <AnimatedCounter target={totalEspacios} label="Espacios únicos" icon="🏡" duration={2000} />
            <AnimatedCounter target={totalReservas} label="Reservas confirmadas" icon="🏢" duration={2200} />
            <AnimatedCounter target="100%" label="Vista al lago" icon="🌊" duration={2000} />
          </div>

          {/* Divisor con título */}
          <div className="flex items-center justify-center mb-8 sm:mb-12">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            <h3 className="px-4 sm:px-6 text-lg sm:text-xl font-semibold text-gray-700">
              Nuestras Instalaciones
            </h3>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>

          {/* Segunda fila: Superficies del camping */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 text-center">
            <AnimatedCounter target="4,100 m²" label="Áreas de mesones" icon="🍽️" duration={2000} />
            <AnimatedCounter target="5,100 m²" label="Áreas de camping" icon="⛺" duration={2100} />
            <AnimatedCounter target="4,900 m²" label="Estacionamientos" icon="🚗" duration={2200} />
            <AnimatedCounter target="1,000 m²" label="Explanada muelle" icon="⚓" duration={2300} />
            <AnimatedCounter target="45 m" label="Muelle" icon="🛥️" duration={2400} />
            <AnimatedCounter target="350 m" label="Frente de playa" icon="🏖️" duration={2500} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern-camping opacity-10" />
        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900">
            ¿Listo para tu aventura en el lago?
          </h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 text-gray-600">
            Reserva tu espacio en Camping Playa Niklitschek y vive la experiencia
            más auténtica del Lago Llanquihue
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <button
              className="w-full sm:w-auto px-8 py-3 rounded-lg font-semibold text-white transition-colors"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.primaryDark
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.primary
              }}
              onClick={() => navigate('/reservar')}
            >
              🏕️ Ver Disponibilidad
            </button>
            <button
              className="w-full sm:w-auto px-8 py-3 rounded-lg font-semibold transition-colors"
              style={{
                borderWidth: '2px',
                borderColor: colors.primary,
                color: colors.primary,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.primary}10`
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent'
              }}
              onClick={() => navigate('/contacto')}
            >
              📞 Contactar
            </button>
          </div>
        </div>
      </section>

      {/* Footer Component */}
      <Footer />

      {/* Scroll Progress Indicator */}
      <ScrollProgressIndicator position="bottom-right" size="medium" showLabel={false} />
    </div>
  )
}

export default HomePage
